import { FC } from "react";
import { FieldInputProps } from "react-final-form";
import { useSelector } from "react-redux";

import { Error } from "@livechat/design-system-icons";
import { IPickerListItem, Icon, Picker } from "@livechat/design-system-react-components";

import { prepareMailboxAddress } from "helpers/mailboxes";
import { shortPickerStyles } from "routes/automations/shared/styles";
import { selectDefaultMailboxAddress, selectLicenseId } from "store/entities/license";
import { IMailbox } from "store/entities/mailboxes/interfaces";
import { selectMailboxesAlphabetically } from "store/entities/mailboxes/selectors";
import { IRuleTriggerSourceMailbox } from "store/entities/rules/interfaces";

import { changeTriggerValues } from "../utils";

interface IProps {
  input: FieldInputProps<IRuleTriggerSourceMailbox>;
}

export const ForwardingTrigger: FC<IProps> = ({ input }) => {
  const defaultMailboxAddress = useSelector(selectDefaultMailboxAddress);
  const mailboxes = useSelector(selectMailboxesAlphabetically);
  const licenseId = useSelector(selectLicenseId);

  const equalityOptions = [createEqualityOption("is"), createEqualityOption("isNot")];

  const selectedEqualityOptions = [createEqualityOption(input.value.invert ? "isNot" : "is")];

  const handleSelectEqualityOption = (selectedOptions: IPickerListItem[] | null): void => {
    if (!selectedOptions || selectedOptions.length === 0) {
      return;
    }

    const selectedOption = selectedOptions[0];
    const newInvert = selectedOption.key === "isNot";
    changeTriggerValues(input, { invert: newInvert });
  };

  const mailboxOptions: IPickerListItem[] = [
    createDefaultMailboxOption(defaultMailboxAddress, licenseId),
    ...mailboxes.map(mailbox => createMailboxOption(mailbox, licenseId)),
  ];

  const selectedMailboxes = input.value.in.map(id => {
    const isDefault = id === "default";

    if (isDefault) {
      return createDefaultMailboxOption(defaultMailboxAddress, licenseId);
    }

    const mailbox = mailboxes.find(mailbox => mailbox.id === id);
    return mailbox ? createMailboxOption(mailbox, licenseId) : createRemovedMailboxOption(id);
  });

  const handleSelectMailbox = (selectedOptions: IPickerListItem[] | null): void => {
    if (!selectedOptions || selectedOptions.length === 0) {
      changeTriggerValues(input, { in: [] });
      return;
    }

    changeTriggerValues(input, { in: selectedOptions.map(option => option.key) });
  };

  return (
    <>
      <Picker
        options={equalityOptions}
        selected={selectedEqualityOptions}
        onSelect={handleSelectEqualityOption}
        hideClearButton
        searchDisabled
        css={shortPickerStyles}
      />
      <Picker
        type="multi"
        options={mailboxOptions}
        selected={selectedMailboxes}
        onSelect={handleSelectMailbox}
        searchDisabled
        placeholder="Select one forwarding address or more…"
      />
    </>
  );
};

const createEqualityOption = (key: "isNot" | "is"): IPickerListItem => ({
  key,
  name: key === "isNot" ? "is not" : "is",
});

const createMailboxOption = (mailbox: IMailbox, licenseId: number): IPickerListItem => ({
  key: mailbox.id,
  name: prepareMailboxAddress(licenseId, mailbox.name),
  customElement: {
    selectedItemBody: <>{`${licenseId}-${mailbox.name}@`}</>,
    listItemBody: <>{prepareMailboxAddress(licenseId, mailbox.name)}</>,
  },
});

const createDefaultMailboxOption = (defaultMailboxAddress: string, licenseId: number): IPickerListItem => ({
  key: "default",
  name: defaultMailboxAddress,
  customElement: {
    selectedItemBody: <>{`${licenseId}@`}</>,
    listItemBody: <>{defaultMailboxAddress}</>,
  },
});

const createRemovedMailboxOption = (removedMailboxId: string): IPickerListItem => ({
  key: removedMailboxId,
  name: "Forwarding address removed",
  selectedTagOptions: {
    kind: "error",
    leftNode: <Icon source={Error} />,
  },
});
